/**
 * The pure half of confirm-to-act (contract v10): what the buttons say and where an
 * action stands, computed from the decision and the plan alone. Kept out of the panel
 * so the tests can pin the words without rendering, and so the component file only
 * exports components.
 */
import { mailFailureReason } from '@/shared/emails/mailFailure';
import { ACTION_WAIT_MS, actedSinceRequest } from '@/shared/emails/useEmails';
import {
  CATEGORY_LABELS,
  DESTINATION_LABELS,
  type EmailCategory,
  type TriageDestination,
} from '@/shared/triage/vocabulary';

import type { TriageDecision, TriagePlan } from './types';

/** A category or destination slug as a person reads it; the slug itself if unknown. */
function slugLabel(slug: string): string {
  return (
    CATEGORY_LABELS[slug as EmailCategory] ?? DESTINATION_LABELS[slug as TriageDestination] ?? slug
  );
}

/**
 * The reasons the activation gives (`activation.py`, every `_no("...")`), matched by
 * shape. Order matters only for the first one: it names an environment variable and
 * would otherwise fall into the generic bucket below.
 */
const GATE_REASONS: readonly (readonly [RegExp, (match: RegExpMatchArray) => string])[] = [
  [/TRIAGE_ACTIONS_ENABLED esta apagado/, () => 'el cerrojo del sistema está cerrado'],
  [/^ya se habia reenviado$/, () => 'ya se había reenviado'],
  [/carpeta de spam/, () => 'el correo está en la carpeta de spam: solo se rescata a mano'],
  [
    /^(\S+) en (\S+) esta en modo solo proponer$/,
    (match) => `en ${match[2]} el sistema solo propone para «${slugLabel(match[1] ?? '')}»`,
  ],
  [
    /^(\S+) en (\S+) esta en modo confirmar/,
    (match) => `«${slugLabel(match[1] ?? '')}» en ${match[2]} espera a que alguien confirme`,
  ],
  [/remitente no esta autenticado/, () => 'el remitente no está autenticado: no se reenvía'],
  [/destino es 'se queda'/, () => 'el destino es «se queda»: no se reenvía nada'],
  // Contract v10.44 (ADR 0038 of the back): up to fifty people get what goes to
  // «Comerciales», so only advertising, and only when a person confirms it.
  [
    /^a 'comerciales' solo se reenvia publicidad$/,
    () => 'a Comerciales solo se reenvía publicidad',
  ],
  [
    /^a 'comerciales' solo se reenvia al pulsar/,
    () => 'a Comerciales solo se reenvía al pulsar «Correcto» o «Corregir a…»',
  ],
  [
    /^no hay direccion configurada para (\S+)$/,
    (match) => `no hay dirección configurada para ${slugLabel(match[1] ?? '')}`,
  ],
  // The label half (`may_label` and `apply_actions`), which reaches the panel through
  // `action_error` as «etiquetado: ...».
  [
    /^etiquetado de (\S+) en (\S+) en modo confirmar/,
    (match) => `«${slugLabel(match[1] ?? '')}» en ${match[2]} espera a que alguien confirme`,
  ],
  [
    /^etiquetado de (\S+) en (\S+) en modo solo proponer$/,
    (match) => `en ${match[2]} el sistema solo propone para «${slugLabel(match[1] ?? '')}»`,
  ],
  [
    /^no hay carpeta configurada para (\S+)$/,
    (match) => `no hay carpeta configurada para «${slugLabel(match[1] ?? '')}»`,
  ],
  [/^ya se habia etiquetado$/, () => 'ya se había copiado a su carpeta'],
  [/^el correo no vino por IMAP$/, () => 'el correo no llegó por un buzón: no hay dónde copiarlo'],
  // Contract v10.25: «Correcto» moves the original, looked up by Message-ID first.
  [/^ya estaba en «(.+)»$/, (match) => `ya está en su carpeta «${match[1] ?? ''}»`],
  // ADR 0026 of the back: a failed redirect keeps the original in the inbox on purpose.
  [
    /^no se mueve porque el reenvio ha fallado/,
    () => 'no se ha movido porque el reenvío falló: sigue en tu bandeja de entrada para reintentar',
  ],
  // The same rule when the destination has no address: nobody got it, so it is not hidden.
  [
    /^no se mueve porque falta la direccion de (\S+?),/,
    (match) =>
      `no se ha movido porque falta la dirección de ${slugLabel(match[1] ?? '')}: sigue en tu bandeja de entrada`,
  ],
  [
    /^el correo ya no esta en la bandeja de entrada del buzon/,
    () =>
      'ya no está en la bandeja de entrada de tu correo (¿lo moviste o lo borraste en tu programa de correo?); lo que has dicho se guarda igual',
  ],
];

/** Anything spelled like an environment variable or a config flag: `WITH_UNDERSCORES`. */
const CONFIG_NAME = /\b[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+\b/;

/**
 * Why a confirmation would not redirect, in plain words (`forward_gate_reason`).
 *
 * The API says it in the operator's language («TRIAGE_ACTIONS_ENABLED esta apagado»),
 * which is right for a log and wrong for the person who just read the email: a variable
 * name tells them nothing they can act on. Known reasons are translated one by one; any
 * other reason that names a variable is folded into «el sistema no está activado para
 * este buzón», and a reason already in plain language passes through as it came.
 */
export function gateReason(reason: string): string {
  for (const [pattern, phrase] of GATE_REASONS) {
    const match = reason.match(pattern);
    if (match) return phrase(match);
  }
  if (CONFIG_NAME.test(reason)) return 'el sistema no está activado para este buzón';
  return reason;
}

/** The real mailboxes a destination stands for, as the back expands them (`_destinations`
 * in `activation.py`): the doubt rule is two deliveries. */
const DESTINATION_PARTS: Partial<Record<TriageDestination, readonly TriageDestination[]>> = {
  paulo_y_administracion: ['paulo', 'administracion'],
};

/**
 * Whether a redirect to `destination` is impossible because an address is missing
 * (contract v10.20, `destinations_without_address`). False with an older back that does
 * not send the list: the panel then says what it said before, no more.
 */
export function lacksAddress(
  plan: TriagePlan | null | undefined,
  destination: TriageDestination | '',
): boolean {
  if (!destination) return false;
  return (plan?.destinations_without_address ?? []).includes(destination);
}

/**
 * «falta la dirección de Paulo»: which half is missing, for the doubt rule too. «Comerciales»
 * has no address of its own (contract v10.44): it is the group of that name, with nobody in.
 */
export function missingAddressText(
  plan: TriagePlan | null | undefined,
  destination: TriageDestination,
): string {
  if (destination === 'comerciales') return 'el grupo «Comerciales» no tiene a nadie';
  const missing = plan?.destinations_without_address ?? [];
  const parts = (DESTINATION_PARTS[destination] ?? [destination]).filter((part) =>
    missing.includes(part),
  );
  const names = (parts.length > 0 ? parts : [destination]).map((each) => DESTINATION_LABELS[each]);
  return names.length > 1
    ? `faltan las direcciones de ${names.join(' y de ')}`
    : `falta la dirección de ${names[0] ?? ''}`;
}

export type ForwardNote = {
  /** Why Correcto will not redirect, in plain words. */
  reason: string;
  /** The one reason a manager fixes in a minute (Administración → Destinos), which the
   * panel says louder than the others. */
  missingAddress: boolean;
  /** The mailbox moves on «Correcto», but not this one until the address is set: the
   * back leaves it in the inbox so it does not go unnoticed (ADR 0026). */
  staysInInbox?: boolean;
};

/** «etiquetado: no se mueve porque falta la direccion de paulo, ...» in the plan. */
const STAYS_FOR_ADDRESS = 'etiquetado: no se mueve porque falta la direccion';

const FORWARD_PREFIX = 'reenvio: ';

/**
 * Why pressing Correcto will not redirect to the proposed destination, or `null` when
 * it will (or when not redirecting is the decision itself, `se_queda`).
 *
 * The button already leaves the redirect out of its words; this says why, because «Va a
 * Paulo» next to a button that only copies to a folder reads as a promise nobody keeps.
 * Seen in production on 2026-09-23 with Paulo's address missing.
 */
export function confirmForwardNote(
  plan: TriagePlan | null | undefined,
  proposed: TriageDestination,
): ForwardNote | null {
  if (!plan || proposed === 'se_queda') return null;
  if ((plan.will_forward_to ?? []).length > 0) return null;
  if (!plan.forward_gate_open) {
    return plan.forward_gate_reason
      ? { reason: gateReason(plan.forward_gate_reason), missingAddress: false }
      : null;
  }
  if (lacksAddress(plan, proposed)) {
    return {
      reason: missingAddressText(plan, proposed),
      missingAddress: true,
      staysInInbox: (plan.reasons ?? []).some((reason) => reason.startsWith(STAYS_FOR_ADDRESS)),
    };
  }
  // An older back (before v10.20): the same fact, from the plan's reasons.
  const forward = (plan.reasons ?? []).find((reason) => reason.startsWith(FORWARD_PREFIX));
  if (!forward) return null;
  const raw = forward.slice(FORWARD_PREFIX.length);
  return { reason: gateReason(raw), missingAddress: raw.startsWith('no hay direccion') };
}

/** The halves of `action_error` («reenvio: ...; etiquetado: ...»), how the panel names
 * each one and how it reads the rest. A failure's technical tail is the server talking:
 * it is read by `mailFailureReason`, and the raw text stays under «Detalles técnicos». */
const ACTION_PARTS: readonly (readonly [
  prefix: string,
  label: string,
  translate: (rest: string) => string,
])[] = [
  ['reenvio fallido: ', 'El reenvío falló', mailFailureReason],
  ['etiquetado fallido: ', 'Guardar en la carpeta falló', mailFailureReason],
  ['reenvio: ', 'Reenvío', gateReason],
  ['etiquetado: ', 'Carpeta', gateReason],
];

/** Whether `action_error` carries a server failure, whose raw text is worth a
 * «Detalles técnicos» (a gate reason is already the whole story). */
export function hasTechnicalTail(error: string): boolean {
  return /fallido: |no se pudo avisar/.test(error);
}

/**
 * `action_error` as a person reads it: one line per half, each reason translated with
 * `gateReason`, and the two halves folded into one line when they say the same thing
 * («Reenvío y carpeta: … espera a que alguien confirme»).
 */
export function actionReasons(error: string): string[] {
  const parts: { label: string | null; reason: string }[] = error.split('; ').map((part) => {
    for (const [prefix, label, translate] of ACTION_PARTS) {
      if (part.startsWith(prefix)) {
        const rest = part.slice(prefix.length);
        return { label, reason: translate(rest) };
      }
    }
    return { label: null, reason: gateReason(part) };
  });
  const [first, second] = parts;
  if (
    parts.length === 2 &&
    first?.label === 'Reenvío' &&
    second?.label === 'Carpeta' &&
    first.reason === second.reason
  ) {
    return [`Reenvío y carpeta: ${first.reason}`];
  }
  return parts.map(({ label, reason }) => (label ? `${label}: ${reason}` : reason));
}

/**
 * What a confirmation is going to do, in the words of the button (contract v10).
 *
 * The person, not the address (contract v10.24, the demo of 2026-09-24: «Correcto:
 * reenviar a paulo@ferreteriacubas.com y copiar a …» read like a line of a log). The
 * addresses go under the button, in `confirmDetail`. `null` plan (no decision, or an older
 * contract) reads as "nothing happens": the button says «Correcto» and nothing else, which
 * is the shadow behaviour of F3.
 */
export function confirmLabel(
  plan: TriagePlan | null | undefined,
  destination: TriageDestination,
): string {
  const forwards = (plan?.will_forward_to ?? []).length > 0;
  const folder = plan?.will_label_as ?? null;
  const moves = Boolean(plan?.will_move);
  const forward = forwards ? `reenviar a ${DESTINATION_LABELS[destination]}` : null;
  // Contract v10.25: the mailbox moves on confirm, and the button says «mover», because
  // the message leaves the inbox of the person's own mail client.
  const file = folder ? `${moves ? 'mover' : 'copiar'} a «${folder}»` : null;
  if (forward && file) {
    // «Paulo y administración» already has its own «y»: a comma keeps the two halves apart.
    return `Correcto: ${forward}${forward.includes(' y ') ? ', y ' : ' y '}${file}`;
  }
  if (file && moves) return `Correcto y ${file}`;
  return forward || file ? `Correcto: ${forward ?? file}` : 'Correcto';
}

/** How the panel names a confirmed action while it runs, when it fails and when it stalls. */
export type ActionWords = {
  /** The line while the worker has it. */
  doing: string;
  /** The start of the alert when it failed: «No se ha reenviado: …». */
  notDone: string;
  /** The alert when the worker has not answered in `ACTION_WAIT_MS`. */
  stalled: string;
};

/**
 * What the confirmed action is, in the words of the waiting line and of the alert.
 *
 * A redirect is the one that leaves the shop, so it names the action whenever there is
 * one. Without it, the mail only moves to its folder (contract v10.25) or only gets a
 * copy there. Before, every action said «Reenviando… el correo va de camino» and «No se
 * ha reenviado», also a «Correcto» on a mail that stays, where nothing is redirected
 * (preview, 2026-09-25). No plan (an older contract) keeps the redirect words.
 */
export function actionWords(plan: TriagePlan | null | undefined): ActionWords {
  const forwards = (plan?.will_forward_to ?? []).length > 0;
  const folder = plan?.will_label_as ?? null;
  const updates = 'esta pantalla se actualiza sola.';
  if (!forwards && folder && plan?.will_move) {
    return {
      doing: `Moviéndolo a «${folder}» en tu correo… ${updates}`,
      notDone: 'No se ha movido',
      stalled: 'El movimiento no ha respondido en dos minutos. Puede que el sistema esté parado.',
    };
  }
  if (!forwards && folder) {
    return {
      doing: `Guardando una copia en «${folder}» de tu correo… ${updates}`,
      notDone: 'No se ha copiado',
      stalled: 'La copia no ha respondido en dos minutos. Puede que el sistema esté parado.',
    };
  }
  return {
    doing: `Reenviando… el correo va de camino; ${updates}`,
    notDone: 'No se ha reenviado',
    stalled: 'El reenvío no ha respondido en dos minutos. Puede que el sistema esté parado.',
  };
}

/**
 * The same action in full, under the button: every address the message goes to and the
 * folder of the owner's own mailbox the copy lands in. «Pedidos cliente» is a folder of
 * the mailbox, not an address, and the button alone did not say so. `null` when the
 * button does nothing but record.
 */
export function confirmDetail(plan: TriagePlan | null | undefined): string | null {
  const to = plan?.will_forward_to ?? [];
  const folder = plan?.will_label_as ?? null;
  if (to.length === 0 && !folder) return null;
  // Several addresses per destination since contract v10.29: «a x, a y y a z».
  const forward =
    to.length > 0
      ? `Se reenvía a ${to.length > 1 ? `${to.slice(0, -1).join(', a ')} y a ${to.at(-1)}` : to[0]}`
      : null;
  if (folder && plan?.will_move) {
    // The notice Eloy asked for (2026-09-24): confirming also files the mail in the
    // person's own mailbox, and nothing is lost.
    const moved = `En tu correo (Thunderbird, el webmail) sale de la bandeja de entrada y queda en la carpeta «${folder}», leído. No se borra nada: puedes devolverlo desde aquí.`;
    return forward ? `${forward}. ${moved}` : moved;
  }
  const copy = folder ? `se guarda una copia en la carpeta «${folder}» de tu correo` : null;
  if (forward && copy) return `${forward} y ${copy}.`;
  return forward
    ? `${forward}.`
    : `${(copy ?? '').charAt(0).toUpperCase()}${(copy ?? '').slice(1)}.`;
}

/**
 * What «Corregir…» will do with the folder, said before saving: moved to the new folder
 * of the person's mail (contract v10.25), copied there, or nothing when the category has
 * no folder.
 */
export function correctionFolderText(
  plan: TriagePlan | null | undefined,
  category: EmailCategory,
): string {
  const folder = plan?.folder_by_category?.[category] ?? null;
  if (!folder) return 'Esta categoría no tiene carpeta.';
  return plan?.moves_on_confirm
    ? `Se moverá a la carpeta «${folder}» de tu correo.`
    : `Se guardará una copia en la carpeta «${folder}» de tu correo.`;
}

/** The label of «Guardar corrección», which redirects when the gate is open. */
export function correctionLabel(
  plan: TriagePlan | null | undefined,
  chosen: TriageDestination | '',
): string {
  if (!plan?.forward_gate_open || !chosen || chosen === 'se_queda') return 'Guardar corrección';
  // Contract v10.20: the gate being open says nothing about the destination having an
  // address, and a button that promises «reenviar a Paulo» with no Paulo is the lie
  // this label exists to avoid.
  if (lacksAddress(plan, chosen)) return 'Guardar corrección';
  return `Guardar y reenviar a ${DESTINATION_LABELS[chosen]}`;
}

export type ActionState =
  | { kind: 'idle' }
  | { kind: 'sending'; since: string }
  | { kind: 'stalled'; since: string }
  | { kind: 'done'; to: string | null; folder: string | null; at: string }
  | { kind: 'failed'; reason: string };

/**
 * Where a confirmed action stands, read off the decision alone (contract v10).
 *
 * `action_requested_at` set and nothing else: the worker has it («Reenviando…»), unless
 * it has been that way longer than `ACTION_WAIT_MS`, in which case the worker is not
 * going to answer and the honest thing is to say so and offer Reintentar. An `acted_at`
 * older than the request answers an earlier one (`actedSinceRequest`) and does not count.
 */
export function actionState(decision: TriageDecision, now = Date.now()): ActionState {
  const requested = decision.action_requested_at ?? null;
  const acted = actedSinceRequest(decision);
  const error = decision.action_error ?? null;
  if (acted && (decision.forwarded_to || decision.labelled_as) && requested) {
    return {
      kind: 'done',
      to: decision.forwarded_to ?? null,
      folder: decision.labelled_as ?? null,
      at: acted,
    };
  }
  if (requested && !acted) {
    if (error && /fallido|no se pudo/.test(error)) return { kind: 'failed', reason: error };
    if (!error) {
      return now - Date.parse(requested) < ACTION_WAIT_MS
        ? { kind: 'sending', since: requested }
        : { kind: 'stalled', since: requested };
    }
  }
  if (!requested && error && /no se pudo avisar/.test(error)) {
    return { kind: 'failed', reason: error };
  }
  return { kind: 'idle' };
}
